/**
 * App Loading Plugin
 * Standalone plugin for managing application loading state without store dependency
 *
 * Usage in components:
 * this.$fullScreenLoading.adjust(1)  // increment loading counter
 * this.$fullScreenLoading.adjust(-1) // decrement loading counter
 * this.$fullScreenLoading.reset()    // reset loading counter to 0
 * this.$fullScreenLoading.isLoading() // check if currently loading
 *
 * Usage globally:
 * window.$this.$fullScreenLoading.adjust(1)
 */

import { reactive } from 'vue'
import Loading from './FullScreenLoading.vue'
import { h, createApp } from 'vue'

class FullScreenLoadingManager {
  constructor() {
    this.state = reactive({
      loadingCounter: 0
    })
    this.loadingVM = null
    this.loadingApp = null
  }

  /**
   * Initialize loading component in the app
   * @param {import('vue').App} vueApp - Vue app instance
   * @param {Object} options - Plugin options
   */
  initialize(vueApp, options) {
    // Mount loading component
    const mountPoint = this._setMountPoint()
    const instanceElement = document.createElement('div')
    mountPoint.appendChild(instanceElement)
    let color = '#00f'
    try{
      color = options.vuetify.theme.computedThemes._value.light.colors.primary
    } catch(e){
      console.warn('[FullScreenLoading] Could not access Vuetify theme colors. Using default color.')
    }


    this.loadingApp = createApp({
      render:()=> {
        return h(Loading, {
          isLoading: this.state.loadingCounter > 0,
          color,
        })
      }
    })

    // Bind state context
    this.loadingApp.config.globalProperties.$state = this.state

    this.loadingVM = this.loadingApp.mount(instanceElement)
  }

  /**
   * Set up mount point for loading component
   * @private
   */
  _setMountPoint() {
    let mountPoint = document.getElementById('app-loading-mount-point')
    if (!mountPoint) {
      mountPoint = document.createElement('div')
      mountPoint.setAttribute('id', 'app-loading-mount-point')
      const appElement = document.querySelector('[data-app="true"]')
      if (appElement) {
        appElement.appendChild(mountPoint)
      } else {
        console.warn('[FullScreenLoading] Could not find <v-app data-app="true"> element. Loading overlay may not render correctly.')
        document.body.appendChild(mountPoint)
      }
    }
    return mountPoint
  }

  adjust(value) {
    if (typeof value !== 'number') {
      console.warn('FullScreenLoadingManager.adjust expects a number, got:', typeof value)
      return
    }
    this.state.loadingCounter += value
    if (this.state.loadingCounter < 0) {
      this.state.loadingCounter = 0
    }
  }

  reset() {
    this.state.loadingCounter = 0
  }

  getState() {
    return this.state.loadingCounter
  }

  isLoading() {
    return this.state.loadingCounter > 0
  }
}

// Global instance
let fullScreenLoadingInstance = null

export default {
  install(app, options) {
    // Create singleton instance if not already created
    if (!fullScreenLoadingInstance) {
      fullScreenLoadingInstance = new FullScreenLoadingManager()
      fullScreenLoadingInstance.initialize(app, options)
    }

    // Register as global property
    app.config.globalProperties.$fullScreenLoading = fullScreenLoadingInstance

    // Also expose as window for backward compatibility
    if (typeof window !== 'undefined') {
      window.$fullScreenLoading = fullScreenLoadingInstance
    }
  }
}
